/**
 * AddEditSubCategoryV2 — full V2 port of the Subcategory edit form,
 * built on the redesign/edit primitives + SortableChildList +
 * QuotesList. Same state shape, fetch graph, and submit handler as
 * v1 so the API contract is byte-identical.
 *
 * Sections:
 *   01 Basic info         — title, parent category, explore term, description
 *   02 Sharing & Media    — subcategory image, deep-link image, universal link
 *   03 Sessions or Quick Taps — sortable list with per-row toggles
 *                                (edit-only; which list depends on
 *                                subcategory_is_quick_tap)
 *   04 Quotes             — multi-line editor with add/remove
 *   05 Danger zone (edit-only)
 *
 * Right rail:
 *   • PublishingCardSub — type / parent / counts pills
 *   • SettingsCardSub   — 4 toggles
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

import LoadingBackdrop from '../../Common/LoadingBackdrop';
import {
  uploadImage,
  clearMedia,
} from '../../../actions/mediaActions';
import {
  fetchOneSubcategory,
  addEditSubCategory,
  deleteSubCategory,
  clearAllSubCategories,
} from '../../../actions/subcategoryActions';
import { fetchAllCategories } from '../../../actions/categoryActions';
import {
  INITIAL_LIST_STATE,
  createSortHandlers,
} from '../../Common/sortableListUtils';
import { preventEnterFormSubmission } from '../../../utils/formUtils';

import { Button } from '../../redesign';
import {
  FormSection,
  FieldBlock,
  FieldRow,
  TextInput,
  Textarea,
  Select,
  LinkField,
  UploadTile,
  SortableChildList,
} from '../../redesign/edit';
import PublishingCardSub from './PublishingCardSub';
import SettingsCardSub from './SettingsCardSub';
import PreviewCardSub from './PreviewCardSub';
import QuotesList from './QuotesList';

const AddEditSubCategoryV2 = forwardRef(function AddEditSubCategoryV2(
  { isEdit, selectedId, onLoaded },
  ref,
) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* ─── State (1:1 with v1) ─────────────────────────────────── */
  const [state, setState] = useState({
    subcategory_id: null,
    subcategory_title: '',
    subcategory_description: '',
    subcategory_explore_term: '',
    subcategory_is_active: true,
    subcategory_is_quick_tap: false,
    subcategory_image: '',
    subcategory_image_preview: '',
    category: 0,
    sessions: [],
    quotes: [],
    isSetToEdit: false,
    subcategory_is_new: false,
    subcategory_is_dark_mode: false,
    subcategory_deep_link_image: '',
    deep_link_image_preview: '',
    universal_link: '',
  });

  const [sessionsState, setSessionsState] = useState(INITIAL_LIST_STATE);
  const [quickTapsState, setQuickTapsState] = useState(INITIAL_LIST_STATE);
  const [sessionData, setSessionData] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const current_subcategory = useSelector((s) => s.subcategories?.current_subcategory);
  const isFetching = useSelector((s) => s.subcategories?.isFetching);
  const all_categories = useSelector((s) => s.categories?.all_categories);

  const handleSessionDataChange = useCallback((id, data) => {
    setSessionData((prev) => ({
      ...prev,
      [id]: {
        is_featured: data.is_featured ?? 0,
        is_dark_mode: data.is_dark_mode ?? 0,
      },
    }));
  }, []);

  /* ─── Data fetching (1:1 with v1) ─────────────────────────── */
  useEffect(() => {
    const fetchData = async () => {
      setState((prev) => ({ ...prev, isSetToEdit: false }));
      dispatch(clearAllSubCategories());
      setSessionsState({ ...INITIAL_LIST_STATE, isLoading: true });
      setQuickTapsState({ ...INITIAL_LIST_STATE, isLoading: true });
      setSessionData({});
      await dispatch(fetchOneSubcategory(selectedId));
    };
    if (isEdit) fetchData();
    return () => {
      setState((prev) => ({ ...prev, isSetToEdit: false }));
      dispatch(clearAllSubCategories());
      setSessionsState(INITIAL_LIST_STATE);
      setQuickTapsState(INITIAL_LIST_STATE);
      setSessionData({});
    };
  }, [dispatch, isEdit, selectedId]);

  useEffect(() => {
    dispatch(fetchAllCategories());
  }, [dispatch]);

  /* Set form state once subcategory data is loaded. */
  useEffect(() => {
    if (!isEdit || !current_subcategory) return;
    if (current_subcategory.subcategory_id !== Number(selectedId)) return;
    setState((prev) => {
      if (prev.isSetToEdit) return prev;
      return {
        ...prev,
        subcategory_id: current_subcategory.subcategory_id,
        subcategory_title: current_subcategory.subcategory_title || '',
        subcategory_description: current_subcategory.subcategory_description || '',
        subcategory_explore_term: current_subcategory.subcategory_explore_term || '',
        subcategory_is_active: current_subcategory.subcategory_is_active ?? true,
        subcategory_is_quick_tap: !!current_subcategory.subcategory_is_quick_tap,
        subcategory_image: current_subcategory.subcategory_image || '',
        subcategory_image_preview: current_subcategory.subcategory_image || '',
        category: current_subcategory.category_id || 0,
        quotes: current_subcategory.quotes || [],
        subcategory_is_new: current_subcategory.subcategory_is_new ?? false,
        subcategory_is_dark_mode: current_subcategory.subcategory_is_dark_mode ?? false,
        subcategory_deep_link_image: current_subcategory.subcategory_deep_link_image || '',
        universal_link: current_subcategory.universal_link || '',
        deep_link_image_preview: current_subcategory.subcategory_deep_link_image || '',
        isSetToEdit: true,
      };
    });

    // Hand title + updated_at up to the page chrome so it doesn't
    // need its own parallel GET /subcategories/{id}.
    if (onLoadedRef.current) {
      onLoadedRef.current({
        subcategory_title: current_subcategory.subcategory_title,
        subcategory_updated_at:
          current_subcategory.subcategory_updated_at
          ?? current_subcategory.updated_at
          ?? null,
      });
    }
  }, [isEdit, current_subcategory, selectedId]);

  /* Process sessions / quick taps onto the sortable lists. */
  useEffect(() => {
    if (!current_subcategory || state.subcategory_is_quick_tap) return;
    const sessions = current_subcategory.sessions || [];
    setSessionsState({
      items: sessions.map((o) => ({
        id: o.session_id + 'SESSION',
        label: o.session_name,
        value: o.session_id + '',
      })),
      isLoaded: true,
      sortOrder: sessions.map((o) => o.session_id + 'SESSION'),
      isLoading: false,
    });
    setSessionData((prev) => ({
      ...prev,
      ...sessions.reduce((acc, s) => {
        acc[s.session_id + ''] = {
          is_featured: s.session_is_featured || 0,
          is_dark_mode: s.session_is_dark_mode || 0,
        };
        return acc;
      }, {}),
    }));
  }, [current_subcategory, state.subcategory_is_quick_tap]);

  useEffect(() => {
    if (!current_subcategory || !state.subcategory_is_quick_tap) return;
    const quickTaps = current_subcategory.quick_taps || [];
    setQuickTapsState({
      items: quickTaps.map((o) => ({
        id: (o.quick_tap_id || o.id) + 'QUICKTAP',
        label: o.quick_tap_title || o.title,
        value: (o.quick_tap_id || o.id) + '',
      })),
      isLoaded: true,
      sortOrder: quickTaps.map((o) => (o.quick_tap_id || o.id) + 'QUICKTAP'),
      isLoading: false,
    });
    setSessionData((prev) => ({
      ...prev,
      ...quickTaps.reduce((acc, q) => {
        acc[(q.quick_tap_id || q.id) + ''] = {
          is_featured: q.is_featured || 0,
          is_dark_mode: q.is_dark_mode || 0,
        };
        return acc;
      }, {}),
    }));
  }, [current_subcategory, state.subcategory_is_quick_tap]);

  /* ─── Field handlers ──────────────────────────────────────── */
  const setField = (name, value) => setState((prev) => ({ ...prev, [name]: value }));
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setField(name, value);
  };

  /* ─── Media uploads ───────────────────────────────────────── */
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (state.subcategory_image_preview) window.URL.revokeObjectURL(state.subcategory_image_preview);
    setState((prev) => ({
      ...prev,
      subcategory_image_preview: window.URL.createObjectURL(file),
    }));
    dispatch(uploadImage(file)).then((response) => {
      setState((prev) => ({ ...prev, subcategory_image: response.data.image_url }));
      dispatch(clearMedia());
    });
  };

  const handleDeepLinkImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (state.deep_link_image_preview) window.URL.revokeObjectURL(state.deep_link_image_preview);
    setState((prev) => ({
      ...prev,
      deep_link_image_preview: window.URL.createObjectURL(file),
    }));
    dispatch(uploadImage(file)).then((response) => {
      setState((prev) => ({ ...prev, subcategory_deep_link_image: response.data.image_url }));
      dispatch(clearMedia());
    });
  };

  /* Preview-URL cleanup. */
  // Latest-value ref for the page chrome's onLoaded callback.
  // Lets the load effect stay keyed on `selectedId` alone.
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  const previewUrlsRef = useRef({});
  previewUrlsRef.current.subcategory_image_preview = state.subcategory_image_preview;
  previewUrlsRef.current.deep_link_image_preview = state.deep_link_image_preview;
  useEffect(() => {
    return () => {
      const refs = previewUrlsRef.current;
      if (refs.subcategory_image_preview) window.URL.revokeObjectURL(refs.subcategory_image_preview);
      if (refs.deep_link_image_preview) window.URL.revokeObjectURL(refs.deep_link_image_preview);
    };
  }, []);

  /* ─── Sort/remove handlers ───────────────────────────────── */
  const sessionsHandlers = useMemo(() => createSortHandlers(setSessionsState), []);
  const quickTapsHandlers = useMemo(() => createSortHandlers(setQuickTapsState), []);

  /* ─── Submit (1:1 with v1) ─────────────────────────────── */
  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!state.subcategory_title) {
      Swal.fire({
        icon: 'error',
        text: 'Subcategory name is required',
        confirmButtonText: 'Ok',
      });
      return;
    }
    setIsSaving(true);

    const {
      quotes: rawQuotes,
      category: category_id,
      subcategory_image_preview,
      deep_link_image_preview,
      ...subcategory_json
    } = state;
    void subcategory_image_preview;
    void deep_link_image_preview;
    const quotes_json = (rawQuotes || []).filter((q) => q.quote_text !== '');

    /* Build session_ids_json from the right sortable list. */
    const activeList = state.subcategory_is_quick_tap ? quickTapsState : sessionsState;
    const session_ids_json = activeList.sortOrder.map((listId) => {
      const item = activeList.items.find((i) => i.id === listId);
      const id = item?.value || '';
      return {
        session_id: id,
        session_is_featured: sessionData[id]?.is_featured || 0,
        session_is_dark_mode: sessionData[id]?.is_dark_mode || 0,
      };
    }).filter((r) => r.session_id);

    const options = {
      quotes_json: JSON.stringify(quotes_json),
      category_id,
      subcategory_json: JSON.stringify(subcategory_json),
      session_ids_json: JSON.stringify(session_ids_json),
    };

    dispatch(addEditSubCategory(options))
      .then(() => {
        dispatch(clearAllSubCategories());
        setSessionsState(INITIAL_LIST_STATE);
        setQuickTapsState(INITIAL_LIST_STATE);
        setSessionData({});
        navigate('/subcategories');
      })
      .catch((error) => {
        console.error('Error saving subcategory:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to save subcategory',
          confirmButtonText: 'Ok',
        });
      })
      .finally(() => setIsSaving(false));
  };

  useImperativeHandle(ref, () => ({
    submit: () => handleSubmit({ preventDefault: () => {} }),
  }));

  /* ─── Delete ─────────────────────────────────────────────── */
  const handleDelete = () => {
    Swal.fire({
      icon: 'warning',
      text: 'Are you sure you want to delete this subcategory?',
      showConfirmButton: true,
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: 'Delete Subcategory',
    }).then((result) => {
      if (result.value) {
        dispatch(deleteSubCategory(state.subcategory_id, () => navigate('/subcategories')));
      }
    });
  };

  /* ─── Category options for the parent picker ─────────────── */
  const categoryOptions = useMemo(() => {
    if (!all_categories) return [];
    const list = Array.isArray(all_categories) ? all_categories : Object.values(all_categories);
    const seen = new Set();
    return list
      .filter((c) => {
        if (!c?.category_id) return false;
        if (seen.has(c.category_id)) return false;
        seen.add(c.category_id);
        return (
          !!c.category_is_quick_tap === !!state.subcategory_is_quick_tap
          && !c.category_is_audiobook
        );
      })
      .map((c) => ({
        value: String(c.category_id),
        label: `${c.category_title || 'Untitled'}${c.category_is_quick_tap ? ' (Quick Tap)' : ''}${!c.category_is_active ? ' (Inactive)' : ''}`,
      }));
  }, [all_categories, state.subcategory_is_quick_tap]);

  /* Parent category title for the publishing card. */
  const parentCategoryTitle = useMemo(() => {
    if (!all_categories || !state.category) return null;
    const list = Array.isArray(all_categories) ? all_categories : Object.values(all_categories);
    const hit = list.find((c) => c.category_id === state.category);
    return hit?.category_title || null;
  }, [all_categories, state.category]);

  /* ─── Render ─────────────────────────────────────────────── */
  if (isEdit && (isFetching || !state.isSetToEdit)) {
    return <LoadingBackdrop open />;
  }

  const activeChildCount = state.subcategory_is_quick_tap
    ? quickTapsState.sortOrder.length
    : sessionsState.sortOrder.length;

  return (
    <>
      <LoadingBackdrop open={isSaving} />
      <div
        className="edit-canvas grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] items-start"
        style={{ gap: 24 }}
      >
        <form
          id="subcategory-form"
          onSubmit={handleSubmit}
          onKeyDown={preventEnterFormSubmission}
          autoComplete="off"
          style={{ minWidth: 0 }}
        >
          {/* 01 Basic info */}
          <FormSection title="Basic info" num="01">
            <FieldBlock label="Subcategory name" required htmlFor="f-sub-title">
              <TextInput
                id="f-sub-title"
                name="subcategory_title"
                value={state.subcategory_title}
                onChange={handleFieldChange}
                required
              />
            </FieldBlock>
            <FieldRow cols={2} minColWidth={240}>
              <FieldBlock label="Parent category" required>
                <Select
                  value={state.category ? String(state.category) : '0'}
                  onChange={(e) => setField('category', e.target.value === '0' ? 0 : Number(e.target.value))}
                >
                  <option value="0">— pick a category —</option>
                  {categoryOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </FieldBlock>
              <FieldBlock
                label="Explore-more search term"
                hint="Powers the in-app Explore More search results"
              >
                <TextInput
                  name="subcategory_explore_term"
                  value={state.subcategory_explore_term || ''}
                  onChange={handleFieldChange}
                />
              </FieldBlock>
            </FieldRow>
            <FieldBlock label="Description" noMargin>
              <Textarea
                name="subcategory_description"
                value={state.subcategory_description || ''}
                onChange={handleFieldChange}
                rows={2}
                placeholder="Optional — shown on the in-app subcategory page."
              />
            </FieldBlock>
          </FormSection>

          {/* 02 Sharing & Media */}
          <FormSection title="Sharing & Media" num="02">
            <FieldRow cols={2} minColWidth={240}>
              <UploadTile
                label="Subcategory Image"
                value={state.subcategory_image_preview || state.subcategory_image}
                accept="image/*"
                onChange={handleImageUpload}
                onRemove={() => {
                  if (state.subcategory_image_preview) window.URL.revokeObjectURL(state.subcategory_image_preview);
                  setState((prev) => ({ ...prev, subcategory_image: '', subcategory_image_preview: '' }));
                }}
              />
              <UploadTile
                label="Deep Link Image"
                hint="Social share card"
                value={state.deep_link_image_preview || state.subcategory_deep_link_image}
                accept="image/*"
                onChange={handleDeepLinkImageUpload}
                onRemove={() => {
                  if (state.deep_link_image_preview) window.URL.revokeObjectURL(state.deep_link_image_preview);
                  setState((prev) => ({ ...prev, subcategory_deep_link_image: '', deep_link_image_preview: '' }));
                }}
              />
            </FieldRow>
            {isEdit && state.universal_link && (
              <div style={{ marginTop: 16 }}>
                <FieldBlock
                  label="Universal Link"
                  hint="Generated automatically · used for share & deep links"
                  noMargin
                >
                  <LinkField value={state.universal_link} />
                </FieldBlock>
              </div>
            )}
          </FormSection>

          {/* 03 Sessions / Quick Taps (edit-only) */}
          {isEdit && (
            state.subcategory_is_quick_tap ? (
              <FormSection
                title="Quick Taps"
                num="03"
                right={
                  <span className="text-text-muted" style={{ fontSize: 11.5 }}>
                    {quickTapsState.sortOrder.length}
                  </span>
                }
              >
                <SortableChildList
                  items={quickTapsState.items}
                  sortOrder={quickTapsState.sortOrder}
                  onRemove={quickTapsHandlers.onRemove}
                  onSortEnd={quickTapsHandlers.onSort}
                  showSwitches
                  onSessionDataChange={handleSessionDataChange}
                  sessionData={sessionData}
                  emptyText="No Quick Taps in this subcategory yet."
                />
              </FormSection>
            ) : (
              <FormSection
                title="Sessions"
                num="03"
                right={
                  <span className="text-text-muted" style={{ fontSize: 11.5 }}>
                    {sessionsState.sortOrder.length}
                  </span>
                }
              >
                <SortableChildList
                  items={sessionsState.items}
                  sortOrder={sessionsState.sortOrder}
                  onRemove={sessionsHandlers.onRemove}
                  onSortEnd={sessionsHandlers.onSort}
                  showSwitches
                  onSessionDataChange={handleSessionDataChange}
                  sessionData={sessionData}
                  emptyText="No sessions in this subcategory yet."
                />
              </FormSection>
            )
          )}

          {/* 04 Quotes */}
          <FormSection
            title="Quotes"
            num={isEdit ? '04' : '03'}
            right={
              <span className="text-text-muted" style={{ fontSize: 11.5 }}>
                {(state.quotes || []).length}
              </span>
            }
          >
            <QuotesList
              quotes={state.quotes}
              onChange={(nextQuotes) => setField('quotes', nextQuotes)}
            />
          </FormSection>

          {/* 05 Danger zone */}
          {isEdit && (
            <FormSection title="Danger zone" num="05">
              <div className="flex items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
                <Button variant="danger" onClick={handleDelete} type="button">
                  Delete subcategory
                </Button>
                <span className="text-text-muted" style={{ fontSize: 12 }}>
                  Permanent — cannot be undone.
                </span>
              </div>
            </FormSection>
          )}
        </form>

        {/* Right rail */}
        <aside
          className="edit-rail"
          style={{
            alignSelf: 'start',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            minWidth: 0,
          }}
        >
          <PreviewCardSub state={state} subitemCount={activeChildCount} />
          {isEdit && (
            <PublishingCardSub
              state={state}
              parentCategoryTitle={parentCategoryTitle}
              childCount={activeChildCount}
              quoteCount={(state.quotes || []).length}
              onToggleActive={(next) => setField('subcategory_is_active', !!next)}
            />
          )}
          <SettingsCardSub
            state={state}
            onChange={(name, value) => setField(name, !!value)}
          />
        </aside>
      </div>
    </>
  );
});

export default AddEditSubCategoryV2;
